import React, { useState } from "react";
import {
  fetchUsers,
  addUser,
  toggleUserStatus,
  changeUserPassword,
  updateUserAuthorities,
} from "./SkeyecubeUsersAPI";
import SkeyecubeUsersTable from "./SkeyecubeUsersTable";
import {
  AddUserDialog,
  ChangePasswordDialog,
  EditAuthoritiesDialog,
} from "./SkeyecubeUsersDialogs";
import PageLayout from "../../../components/PageLayout";
import SearchToolbar from "../../../components/SearchToolbar";
import usePageTitle from "../../../services/usePageTitle";
import usePageState from "../../../hooks/usePageState";
import useIsRoleUser from "../../../hooks/useIsRoleUser";
import skeyecubeUsersHelp from "../../../content/help/skeyecube-users.json";

export default function SkeyecubeUsers({ pageTitle = "Skeyecube Users" }) {
  usePageTitle("SkeyeCube Users - SkeyeGate™");

  const {
    filteredData,
    loading,
    searchText,
    page,
    pageSize,
    messageApi,
    contextHolder,
    handleSearch,
    clearFilter,
    loadData,
    onPageChange,
  } = usePageState({
    fetchFn: fetchUsers,
    filterFields: ["name", (u) => u.groups?.join(" ")],
  });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [editAuthModalOpen, setEditAuthModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const isRoleUser = useIsRoleUser();

  const handleToggleStatus = async (username, currentDisabled) => {
    try {
      await toggleUserStatus(username, currentDisabled);
      messageApi.success(
        `User ${currentDisabled ? "enabled" : "disabled"} successfully`,
      );
      loadData();
    } catch (error) {
      messageApi.error("Failed to toggle user status");
      console.error("Toggle status error:", error);
    }
  };

  const handleAddUser = async (values) => {
    try {
      await addUser(values);
      messageApi.success("User created successfully");
      loadData();
    } catch (error) {
      console.error("Create user error:", error);
      throw error;
    }
  };

  const handleChangePassword = async (username, newPass, repeat) => {
    try {
      const response = await changeUserPassword(username, newPass, repeat);
      if (typeof response === "string") {
        const respObj = JSON.parse(response);
        if (respObj.code !== "000")
          throw new Error(respObj.msg || "Failed to change password");
      }

      messageApi.success("Password changed successfully");
    } catch (error) {
      console.error("Change password error:", error);
      throw error;
    }
  };

  const handleEditAuthorities = async (username, payload) => {
    try {
      await updateUserAuthorities(username, payload);
      messageApi.success("User authorities updated successfully");
      loadData();
    } catch (error) {
      console.error("Edit authorities error:", error);
      throw error;
    }
  };

  return (
    <PageLayout
      title={pageTitle}
      logo="/img/majesteye-icon-skeye-cube-square-white-background-no-label"
      description="Manager for Skeyecube users, allowing you to create, edit, and organize your user accounts and permissions in one place."
      helpContent={skeyecubeUsersHelp}
    >
      {contextHolder}
      <SearchToolbar
        search={searchText}
        onSearchChange={handleSearch}
        onClearFilter={clearFilter}
        addButtonLabel="Add User"
        onAdd={() => setAddModalOpen(true)}
        showAdd={!isRoleUser}
      />
      <SkeyecubeUsersTable
        data={filteredData}
        loading={loading}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onToggleStatus={handleToggleStatus}
        onEditUser={(user) => {
          setSelectedUser(user);
          setEditAuthModalOpen(true);
        }}
        onChangePassword={(user) => {
          setSelectedUser(user);
          setPasswordModalOpen(true);
        }}
        isRoleUser={isRoleUser}
      />
      <AddUserDialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddUser}
      />
      <ChangePasswordDialog
        open={passwordModalOpen}
        user={selectedUser}
        onClose={() => setPasswordModalOpen(false)}
        onSave={handleChangePassword}
      />
      <EditAuthoritiesDialog
        open={editAuthModalOpen}
        user={selectedUser}
        onClose={() => setEditAuthModalOpen(false)}
        onSave={handleEditAuthorities}
      />
    </PageLayout>
  );
}
